import { toColor, toGlyphName, type Color, type GlyphName, type IconName } from "@salestrak/ui";

import {
  isEntityViewType,
  type EntityViewRow,
  type EntityViewType,
} from "#app/lib/data";

/**
 * The identity a view carries before anyone renames it: tab label, glyph and
 * chip color, per view type.
 *
 * The server seeds the same three onto a scope's built-in views
 * (`entity-view-defaults.ts`). This copy is the client's answer for the two
 * cases the row cannot cover — a stored icon that is null, and the "add a
 * view" menu, which needs a label for a view that does not exist yet.
 */
export const VIEW_TYPE_META: Record<
  EntityViewType,
  {
    label: string;
    icon: IconName;
    tabIcon: IconName;
    color: Color;
    /** A tabbed scope keeps at least one view of this type — the delete
     *  floor the server enforces too (`REQUIRED_ENTITY_VIEW_TYPES`). List
     *  and Board are how a workflow's items are worked; a dashboard is a
     *  report on them, so a workflow may hold none. */
    required: boolean;
  }
> = {
  list: {
    label: "List",
    icon: "list",
    tabIcon: "view-list",
    color: "grey",
    required: true,
  },
  board: {
    label: "Board",
    icon: "column-3",
    tabIcon: "view-board",
    color: "blue",
    required: true,
  },
  dashboard: {
    label: "Dashboard",
    icon: "bar-chart-increasing",
    tabIcon: "view-dashboard",
    color: "orange",
    required: false,
  },
};

/** A view's seed identity — what its type carries before anyone customizes
 *  it, and what a stored null resolves to. */
export function viewTypeSeed(
  view: EntityViewRow,
): (typeof VIEW_TYPE_META)[EntityViewType] {
  return isEntityViewType(view.type)
    ? VIEW_TYPE_META[view.type]
    : VIEW_TYPE_META.list;
}

/** A row's stored glyph, narrowed, falling back to its type's seed. */
export function viewVisual(view: EntityViewRow): {
  icon: GlyphName;
  color: Color;
} {
  const fallback = viewTypeSeed(view);
  return {
    icon: toGlyphName(view.icon.icon) ?? fallback.icon,
    color: toColor(view.icon.color) ?? fallback.color,
  };
}

/** A default icon keeps the structural color tile. A user-selected icon leaves
 *  the tile and draws as a bare glyph in the view's chosen color. */
export function viewTabVisual(view: EntityViewRow): {
  icon: GlyphName;
  color: Color;
  custom: boolean;
} {
  const seed = viewTypeSeed(view);
  const visual = viewVisual(view);
  return visual.icon === seed.icon
    ? { icon: seed.tabIcon, color: visual.color, custom: false }
    : { icon: visual.icon, color: visual.color, custom: true };
}
